import { useMutation } from '@apollo/client';
import { metrics } from '@seek/metrics-js';
import { Inline, Stack, useResponsiveValue } from 'braid-design-system';
import type React from 'react';
import {
  forwardRef,
  useEffect,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';

import type { RoleRequirementsEvent } from '../../analytics';
import { useDebounce } from '../../hooks/useDebounce';
import {
  type JobDetails,
  QuestionSource,
  type SelectedQuestion,
  WorkType,
} from '../../sdk';
import {
  type Actions,
  addCustomQuestion,
  deleteCustomQuestion,
  type StatefulRecommendedQuestion,
} from '../App/state';
import { AnalyticsContext } from '../RoleRequirements/RoleRequirements';

import { AddCustomQuestion } from './AddCustomQuestion/AddCustomQuestion';
import { CustomQuestionHeader } from './CustomQuestionHeader/CustomQuestionHeader';
import {
  CustomQuestionType,
  DraftCustomQuestion,
  isDraftCustomQuestion,
} from './DraftCustomQuestion/DraftCustomQuestion';
import {
  customQuestionIdLocation,
  mapRecommendedQuestionToCustomQuestion,
} from './mapping';
import {
  ADD_CUSTOM_QUESTION,
  type CreateCustomQuestion,
  type CreateCustomQuestionVariables,
} from './operations';
import { persistCustomQuestions } from './save';
import type { CustomQuestionResponse } from './types';

interface Props {
  onChange?: (selections: SelectedQuestion[]) => void;
  onEvent?: (event: RoleRequirementsEvent) => void;
  questions: StatefulRecommendedQuestion[];
  dispatch: React.Dispatch<Actions>;
  customQuestionsSelectionLimit: number;
  onDraftCustomQuestionsUpdated: (texts: string[]) => void;
}

export interface CustomQuestionsWidget {
  save: () => Promise<CustomQuestionResponse[]>;
}

const CustomQuestions: React.ForwardRefRenderFunction<
  CustomQuestionsWidget,
  Props
> = (
  {
    questions,
    customQuestionsSelectionLimit,
    dispatch,
    onDraftCustomQuestionsUpdated,
  },
  ref,
) => {
  useEffect(() => {
    metrics.count('display-custom-questions');
  }, []);

  const [draftCustomQuestions, setDraftCustomQuestions] = useState<
    DraftCustomQuestion[]
  >([]);
  /**
   * This will be used for `onDraftCustomQuestionsUpdated` event handler.
   * We want to debounce so that the event handler won't be triggered
   * every time a user type in something into custom question text field
   * and thus improve the performance for the consumer of this props.
   */
  const debouncedDraftCustomQuestions = useDebounce(draftCustomQuestions, 500);

  useEffect(() => {
    onDraftCustomQuestionsUpdated(
      debouncedDraftCustomQuestions
        .filter(({ text }) => text.trim() !== '')
        .sort((a, b) => a.order - b.order)
        .map((question) => question.text),
    );
  }, [debouncedDraftCustomQuestions, onDraftCustomQuestionsUpdated]);

  const [createCustomQuestion] = useMutation<
    CreateCustomQuestion,
    CreateCustomQuestionVariables
  >(ADD_CUSTOM_QUESTION);
  const analytics = useContext(AnalyticsContext);
  const createCustomQuestions = persistCustomQuestions(createCustomQuestion);

  const save = async (): Promise<CustomQuestionResponse[]> => {
    const duplicates = getDuplicates();

    const dedupedDraftCustomQuestions = draftCustomQuestions.filter(
      (dcq) => !duplicates.some((ddcq) => ddcq.position === dcq.position),
    );

    const responses = await createCustomQuestions(dedupedDraftCustomQuestions);

    for (const result of responses) {
      switch (result.status) {
        case 'complete':
          const existingDraft = draftCustomQuestions.find(
            (x) => x.position === result.position,
          );
          dispatch(
            addCustomQuestion(result.question, existingDraft?.order ?? 0),
          );
          deleteDraftQuestion(result.position);
          continue;
        case 'illegal':
          updateDraftCustomQuestion(result.position, {
            error: result.reason,
          });
          continue;
        case 'apiError':
          updateDraftCustomQuestion(result.position, {
            error: 'Sorry, we could not create this question',
          });
          continue;
      }
    }

    return responses.concat(duplicates);
  };

  const getDuplicates = (): CustomQuestionResponse[] => {
    const duplicatedDraftCustomQuestions = draftCustomQuestions.filter(
      (draftCustomQuestion) =>
        questions.some(
          (q) => q.text === draftCustomQuestion.text && q.selected,
        ) ||
        draftCustomQuestions.some(
          (q) =>
            q.text === draftCustomQuestion.text &&
            q.position !== draftCustomQuestion.position,
        ),
    );

    if (
      duplicatedDraftCustomQuestions &&
      duplicatedDraftCustomQuestions.length > 0
    ) {
      for (const duplicatedDraftCustomQuestion of duplicatedDraftCustomQuestions) {
        updateDraftCustomQuestion(duplicatedDraftCustomQuestion.position, {
          error: 'Your question is a duplicate',
        });
      }

      return duplicatedDraftCustomQuestions.map((ddcq) => ({
        status: 'illegal',
        location: customQuestionIdLocation(ddcq.position),
        position: ddcq.position,
        reason: 'Duplicated question text',
      }));
    }

    return [];
  };

  useImperativeHandle(ref, () => ({
    save,
  }));

  const deleteDraftQuestion = (index: number) => {
    setDraftCustomQuestions((existing) =>
      existing.filter((x) => x.position !== index),
    );
  };

  const deleteExistingQuestion = (question: { id: string }) => {
    dispatch(deleteCustomQuestion(question.id));
  };

  const migrateExistingQuestionToDraftAndUpdate = (
    existingQuestion: StatefulRecommendedQuestion,
    changes: Partial<DraftCustomQuestion>,
  ) => {
    dispatch(deleteCustomQuestion(existingQuestion.id));

    setDraftCustomQuestions((existing) => [
      ...existing,
      {
        ...mapRecommendedQuestionToCustomQuestion(
          existingQuestion,
          existing.length,
        ),
        ...changes,
      },
    ]);
  };

  const createNewDraftCustomQuestion = (order: number) => {
    setDraftCustomQuestions((existing) => [
      ...existing,
      {
        position:
          existing.length === 0
            ? 0
            : existing[existing.length - 1].position + 1,
        text: '',
        source: QuestionSource.Custom,
        type: CustomQuestionType.FreeText,
        options: [],
        order,
      },
    ]);
  };

  const customQuestions = [
    ...questions.filter((q) => q.source === QuestionSource.Custom),
    ...draftCustomQuestions,
  ].sort((a, b) => a.order - b.order);

  const updateDraftCustomQuestion = (
    position: number,
    changes: Partial<DraftCustomQuestion>,
  ) => {
    setDraftCustomQuestions((existing) => {
      const newIndex = existing.findIndex((x) => x.position === position);
      const existingQuestion = existing[newIndex];
      return [
        ...existing.slice(0, newIndex),
        { ...existingQuestion, ...changes },
        ...existing.slice(newIndex + 1),
      ];
    });
  };

  const addDraftCustomQuestion = () => {
    createNewDraftCustomQuestion(
      (customQuestions.slice(-1)[0]?.order ?? 0) + 1,
    );
    analytics.sendQuestionAddedEvent();
  };

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
    desktop: false,
    wide: false,
  });

  const customQuestionsStackSpace = isMobile ? 'large' : 'xlarge';

  return (
    <Stack
      space={
        customQuestions.length === 0 ? 'medium' : customQuestionsStackSpace
      }
    >
      <CustomQuestionHeader
        selected={customQuestions.length}
        customQuestionsSelectionLimit={customQuestionsSelectionLimit}
      />
      {customQuestions.map((question, index) => (
        <DraftCustomQuestion
          validationFailures={[]}
          key={index}
          id={customQuestionIdLocation(index)}
          question={
            isDraftCustomQuestion(question)
              ? question
              : mapRecommendedQuestionToCustomQuestion(question, index)
          }
          order={index + 1}
          isMobile={isMobile}
          onDelete={() => {
            analytics.sendQuestionDeletedEvent();
            return isDraftCustomQuestion(question)
              ? deleteDraftQuestion(question.position)
              : deleteExistingQuestion(question);
          }}
          onChange={(changes) =>
            isDraftCustomQuestion(question)
              ? updateDraftCustomQuestion(question.position, changes)
              : migrateExistingQuestionToDraftAndUpdate(question, changes)
          }
        />
      ))}
      {customQuestions.length < customQuestionsSelectionLimit &&
        (isMobile ? (
          <AddCustomQuestion
            customQuestions={customQuestions}
            addDraftCustomQuestion={addDraftCustomQuestion}
          />
        ) : (
          <Inline space="small">
            <AddCustomQuestion
              customQuestions={customQuestions}
              addDraftCustomQuestion={addDraftCustomQuestion}
            />
          </Inline>
        ))}
    </Stack>
  );
};

CustomQuestions.displayName = 'CustomQuestions';

const ForwardedRefCustomQuestions = forwardRef(CustomQuestions);

export {
  ForwardedRefCustomQuestions as CustomQuestions,
  WorkType,
  type JobDetails,
};
